import React from "react";
import { useSearchParams } from "react-router-dom";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
import { buildDetailPath } from "@shared/navigation/tabs/detailRoutes";
import { EditableTagList } from "../Shared/NameTagEdit/EditableTagList";
import { useMetaEditor } from "../Shared/NameTagEdit/useMetaEditor";
import { DetailRow } from "../Shared/DetailRow";
import { DetailLink } from "../Shared/DetailLink";
import ExtenderPopLinkBandwidthChart from "./ExtenderPopLinkBandwidthChart";
import ExtenderPopConnections from "./ExtenderPopConnections";
import CarrierAccessSummary from "./CarrierAccessSummary";
import CpeLinksTable from "./CpeLinksTable";
import { cpeLinkRows, soldCpeLinkRows } from "./extenderPopCpeLinks";
import { extenderPopStatusPresentation } from "./ExtenderPopData";
import {
  LoadingDetail,
  RoleTag,
  legacyTagsNoop,
  linkLabel,
  useExtenderPopDetail,
} from "./ExtenderPopDetailShared";

/**
 * Detail of one CPE (80-/83-) of an Extended PoP: its own name and tags
 * (CTX-832), its links with each carrier's status, one bandwidth chart per
 * link, and the connections it delivers. The site's detail keeps the
 * summary; this is where the CPE is looked at closely.
 *
 * It reads its CPE out of the site's detail — the same request and cache
 * entry — so it paints at once when opened from the site.
 */
const ExtenderPopCpeDetails: React.FC = () => {
  const [searchParams] = useSearchParams();
  const extenderPopId = Number(searchParams.get("extenderPopId"));
  const cpeId = Number(searchParams.get("cpeId"));
  const { tenantId } = useAppSelector((state) => state.selection);
  const api = useAxiosAuth0();

  const { data: site, error } = useExtenderPopDetail(extenderPopId, tenantId);
  const cpe = site?.cpes.find((candidate) => candidate.id === cpeId) ?? null;

  const meta = useMetaEditor({
    api,
    kind: "extender-pop-cpe",
    resourceId: cpe ? String(cpe.id) : null,
    parentId: String(extenderPopId),
    tenantId,
    initialName: cpe?.name ?? "",
    initialTag: "",
  });

  const saveName = (newValue: string) => {
    const nextName = newValue.trim();
    if (!nextName) {
      meta.cancelName();
      return;
    }
    meta.setNameValue(nextName);
    meta.saveName(nextName).catch(() => meta.cancelName());
  };

  if (error) return <ErrorFetchingItems text={error} />;
  if (!site) return <LoadingDetail />;
  if (!cpe) {
    return <ErrorFetchingItems text="This device is not part of the CPE" />;
  }

  const status = extenderPopStatusPresentation(cpe.status);
  // Once the router has a sold Backhaul, only its links (and the HA
  // interconnect) show — in the table and in the charts alike.
  const linkRows = soldCpeLinkRows(cpeLinkRows(cpe.links, cpe.carrierAccess));
  const shownLinkIds = new Set(linkRows.map((row) => row.linkId));

  return (
    <div className="px-4 overflow-y-auto h-full">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4 auto-rows-auto">
        <div className="flex w-full self-start">
          <GenericCard
            isCollapsible={false}
            headerContent="CPE Device Details"
            bodyContent={
              <div className="space-y-2">
                <DetailRow label="ID" value={cpe.serviceId} />
                <DetailRow
                  label="Name"
                  value={
                    <GenericTag
                      label={meta.nameValue || "—"}
                      variant="neutral"
                      size="small"
                      minWidth="100px"
                      className="text-center"
                      editable={true}
                      value={meta.nameValue}
                      onSave={saveName}
                      onCancel={meta.cancelName}
                      isSaving={meta.savingField === "name"}
                      disabled={meta.savingField !== null}
                    />
                  }
                />
                <div className="flex justify-between w-full py-1 border-b">
                  <h3 className="text-sm text-semantic-text-primary">Tag</h3>
                  <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
                    <EditableTagList
                      tags={[]}
                      onUpdate={legacyTagsNoop}
                      resourceType="ke-extender-pop-cpe"
                      resourceId={cpe.id}
                      tenantId={site.tenantId}
                    />
                  </div>
                </div>
                <DetailRow
                  label="CPE"
                  value={
                    <DetailLink
                      to={buildDetailPath("extenderPop", { extenderPopId: site.id })}
                      label={site.name ? `${site.serviceId} · ${site.name}` : site.serviceId}
                    />
                  }
                />
                <DetailRow
                  label="Backhaul"
                  value={
                    cpe.carrierAccess.length > 0 ? (
                      <CarrierAccessSummary carriers={cpe.carrierAccess} align="end" />
                    ) : null
                  }
                  isLast
                />
              </div>
            }
          />
        </div>

        <div className="flex w-full self-start">
          <GenericCard
            isCollapsible={false}
            headerContent="Status"
            bodyContent={
              <div className="space-y-2">
                <DetailRow
                  label="Service Status"
                  value={
                    <GenericTag
                      variant={status.variant}
                      label={status.label}
                      size="small"
                    />
                  }
                />
                {/* The contract (term, renewal) is the CPE's, on its detail. */}
                <DetailRow
                  label="Role"
                  value={cpe.role ? <RoleTag role={cpe.role} /> : null}
                  isLast
                />
              </div>
            }
          />
        </div>

        <div className="flex w-full self-start md:col-span-2">
          <GenericCard
            isCollapsible={false}
            headerContent="Links"
            bodyContent={<CpeLinksTable rows={linkRows} />}
          />
        </div>
      </div>

      <div className="flex flex-col gap-4 pb-4">
        {cpe.links
          .filter((link) => shownLinkIds.has(link.portAllocationId))
          .map((link) => (
          <ExtenderPopLinkBandwidthChart
            key={link.portAllocationId}
            extenderPopId={site.id}
            linkId={link.portAllocationId}
            title={`${cpe.serviceId} · ${linkLabel(link)}`}
            tenantId={tenantId}
          />
        ))}
      </div>

      <div className="pb-4">
        <ExtenderPopConnections
          extenderPopId={site.id}
          tenantId={tenantId}
          cpes={site.cpes}
          cpeId={cpe.id}
        />
      </div>
    </div>
  );
};

export default ExtenderPopCpeDetails;
